import * as d3 from 'd3';
import * as _ from 'lodash';
import * as moment from 'moment';

import { StatusChartMode } from './StatusChartMode';
import { SvgManager } from '../../SvgManager';
import { IChart } from '../../interfaces/IChart';
import { RoundingHelper, ILogger, LoggerFactory, DateFormatHelper } from '../../../helpers/helpers';
import { getConfig } from '../../helpers/rectangleChartHelper';
import { ChartBase } from '../ChartBase';
import { IChartOptions, Margins } from '../../../models';
import { FakeSeries } from '../../pieces/series';
import { greyTextColor } from '../../../../common/constants';
import { setSizeClasses } from '../setSizeClasses';

export interface IStatusChart extends IChart {
}

export class StatusChart extends ChartBase implements IStatusChart {
    private _dateFormat = d3.time.format('%Y-%m-%d %H:%M');
    private _dispatch: any;
    private _format = d3.format('%');
    private _groupHeight: number;
    private _groupWidth: number;
    private _lastData: any;
    private _logger: ILogger;
    private _manager: SvgManager;
    private _margin = 20;
    private _mode: StatusChartMode;
    private _rectHeight = 20;
    private _rectWidth: number;
    private _textHeight = 10;
    private _timeAxis: d3.svg.Axis;
    private _timeScale: d3.time.Scale<number, number>;
    private _valuesHeight: number;

    // Computed properties must be after the inital value of others
    private _groupMargins = {
        top: this._rectHeight / 4,
        bottom: 3 * this._rectHeight / 4 + this._textHeight,
        left: this._margin,
        right: this._margin
    };
    private _valuesMargin = { top: this._rectHeight / 2, bottom: this._textHeight, left: 0, right: 0 };


    constructor(chartOptions: IChartOptions, svgManager: SvgManager) {
        super(chartOptions, svgManager, 'status-chart', false);
        try {
            this._logger = LoggerFactory.getLogger('WideTech.Charting.statusChart', false, 0);
            this._dispatch = d3.dispatch('dateChanged');
            this.init(svgManager);
        } catch (e) {
            this._logger.error(e);
        }
    }

    private init(svgManager: SvgManager) {
        this._mode = StatusChartMode.Frequency;
        this.resizeFunction(this.applyResize());
        this.computeSizes(this.width(), this.height());
        this._manager = svgManager;
        this._timeScale = d3.time.scale<number, number>()
            .range([0, this._rectWidth]);
        this._timeAxis = d3.svg.axis()
            .scale(this._timeScale);
        this.legend().margins(new Margins(20, 0, 0, 0));
        this.isTitleVisible(false);
        const size = this._manager.getSize();
        this.resize()(size.width, size.height);
    }

    private applyResize() {
        const self = this;
        return () => {
            self.executeHandled(() => {
                self._logger.debug('Resizing.');
                self.removePanes();
                const width = this.width();
                const height = this.height();
                if (width && height) {
                    this.computeSizes(width, height);
                    setSizeClasses(this.group(), width, height, this.ratio() || 1);
                    const range: [number, number] = [0, this._rectWidth];
                    const dataSelection = this.group().selectAll('.group-result');
                    this.updateFrequency(dataSelection, range);
                    this._timeScale.range(range);
                    this.updateAxis(dataSelection);
                    this.drawRanges(this.group().select('group-value'), this._rectWidth, this._rectHeight);
                    this.group()
                        .selectAll('.crosshair')
                        .attr('height', this._rectHeight);
                    this.group()
                        .selectAll('.crosshair-bg')
                        .attr('width', this._rectWidth)
                        .attr('height', this._rectHeight);
                    this.updateLayout(this.group().selectAll('.group-result'));
                    self._logger.debug('Resized.');
                }
            });
        };
    }

    private computeSizes(width: number, height: number) {
        try {
            const ratio = this.ratio() || 1;
            let nData = 1;
            if (this._lastData && this._lastData.length) {
                nData = this._lastData.length;
            }
            let legendWidth = 0;
            if (this.legend() && this.legend().isVisible() && this.legend().width() > 0) {
                legendWidth = this.legend().width();
                if (legendWidth < 150 * ratio) {
                    legendWidth = Math.min(legendWidth + 4 * ratio, 150 * ratio);
                }
            }

            const config = getConfig(nData, width, height, this._margin, this.ratio() || 1);
            const textHeight = this._textHeight * ratio;
            this._rectHeight = config.rectHeight;
            this._groupMargins.top = this._rectHeight / 4;
            this._groupMargins.bottom = 3 * this._rectHeight / 4;
            const groupMarginsLeft = this._groupMargins.left * ratio;
            const groupMarginsRight = this._groupMargins.right * ratio;
            this._valuesMargin.top = this._rectHeight / 2;
            this._valuesMargin.bottom = textHeight;
            const valuesMarginLeft = this._valuesMargin.left * ratio;
            const valuesMarginRight = this._valuesMargin.right * ratio;
            this._groupWidth = width - groupMarginsLeft - groupMarginsRight;
            this._rectWidth = Math.max(this._groupWidth - valuesMarginLeft - valuesMarginRight - legendWidth, 0);
            const labelHeight = textHeight;
            this._valuesHeight = this._rectHeight + this._valuesMargin.top + this._valuesMargin.bottom;
            this._groupHeight = labelHeight + this._valuesHeight + this._groupMargins.top
                + this._groupMargins.bottom;
            if (this._manager) {
                this._manager.setHeight(nData * this._groupHeight);
            }
        } catch (e) {
            this._logger.error(e);
        }
    }

    private updateFrequency(dataSelection: d3.Selection<any>, range: [number, number]) {
        try {
            this._logger.debug('Updating frequency');
            const ratio = this.ratio() || 1;
            const xScale = d3.scale.linear()
                .range(range)
                .domain([0, 1]);
            const groupMarginsLeft = this._groupMargins.left * ratio;
            const groupMarginsRight = this._groupMargins.right * ratio;
            dataSelection
                .attr('transform', (d, i) => {
                    return `translate(${groupMarginsLeft},${i * (this._groupHeight)
                        + this._groupMargins.top})`;
                });
            dataSelection.select('.border')
                .attr('width', this._rectWidth);
            const valuesRect = dataSelection
                .selectAll('.value-rect')
                .attr('transform', d => {
                    const x0 = (d.x0 && !isNaN(d.x0)) ? d.x0 : 0;
                    return `translate(${xScale(x0)},0)`;
                });
            valuesRect.select('rect')
                .attr({
                    'width': d => xScale(d.value)
                });
            this.updateLabels(valuesRect, xScale, this._rectHeight);
        } catch (e) {
            this._logger.error(e);
        }
    }

    private updateAxis(dataSelection: d3.Selection<any>) {
        try {
            this._logger.debug('Updating axis');
            const helper = new DateFormatHelper();
            const domain = this._timeScale.domain();
            const range = this._timeScale.range();
            const duration = moment.duration(moment(domain[1])
                .diff(moment(domain[0])));
            const fmt = helper.format(duration);
            // Pretend the chart is wider...
            const ticks = helper.getTicks(domain as any, range[1] / (this.ratio() || 1), duration, fmt.length);
            this._timeAxis.tickValues(ticks)
                .tickFormat(d3.time.format(fmt.format));
            dataSelection.select('g.axis')
                .call(this._timeAxis);
            dataSelection.select('g.axis')
                .selectAll('.tick')
                .select('text')
                .classed('tickLabel', true);
        } catch (e) {
            this._logger.error(e);
        }
    }

    private drawValues(dataSelection: d3.Selection<any>, width: number, height: number) {
        try {
            this._logger.debug('Drawing values');
            const ratio = this.ratio() || 1;
            const xScale = d3.scale.linear()
                .range([0, width])
                .domain([0, 1]);
            const valuesGroup = dataSelection
                .selectAll('.value-rect')
                .data(d => d ? this.reduce(d.percentages) : []);
            valuesGroup.exit()
                .remove();
            const enterSelection = valuesGroup.enter()
                .append('g')
                .classed('value-rect', true);
            valuesGroup.attr('transform', d => `translate(${(d && d.x0 && !isNaN(d.x0)) ? xScale(d.x0) : 0},0)`);
            enterSelection.append('rect')
                .style('opacity', 1);
            valuesGroup
                .select('rect')
                .attr({
                    'width': d => xScale(d.value),
                    'height': height
                })
                .style({ 'fill': d => d.color });
            enterSelection
                .append('g')
                .classed('tick', true)
                .append('text')
                .classed('tickLabel', true)
                .style('text-anchor', 'middle')
                .attr('y', 9 * ratio) //same values as in d3 scales
                .attr('dy', '.71em');
            enterSelection.append('title');
            this.updateLabels(valuesGroup, xScale, height);
        } catch (e) {
            this._logger.error(e);
        }
    }

    private updateLabels(group: d3.Selection<any>, xScale: d3.scale.Linear<number, number>, height: number) {
        try {
            this._logger.debug('Updating labels');
            const ratio = this.ratio() || 1;
            const minWidth = 20 * ratio;
            group
                .select('.tick')
                .attr('transform', d => `translate(${0},${height})`)
                .select('text')
                .attr('x', d => xScale(d.value) / 2)
                .text(d => {
                    let label = '';
                    const scale = xScale(d.value);
                    const nChar = d.name.length;
                    // 6 added for ' - xx%'
                    if (scale > (nChar + 6) * 7 * ratio) {
                        label = d.name + ' - ' + this._format(d.value);
                    } else if (scale > minWidth) {
                        label = this._format(d.value);
                    }
                    return label;
                });
            group
                .select('title')
                .text(d => d.name + ' - ' + this._format(d.value));
        } catch (e) {
            this._logger.error(e);
        }
    }

    private reduce(percentages: any[]): any[] {
        const reduced = [];
        if (percentages) {
            percentages
                .sort((a, b) => b.value - a.value);
            const _reduced = percentages
                .reduce((a, b, i) => {
                    const sum = a + b.value;
                    reduced[i] = {
                        color: b.color,
                        value: b.value,
                        x0: a,
                        x1: sum,
                        name: b.name
                    };
                    return sum;
                }, 0);
        }
        return reduced;
    }

    private drawRanges(dataSelection: d3.Selection<any>, _width: number, height: number) {
        const xScale = this._timeScale;
        this.updateAxis(dataSelection);
        const valuesGroup = dataSelection
            .selectAll('.range-rect')
            .data(d => d.ranges as any[]);
        valuesGroup.exit()
            .remove();
        const enterSelection = valuesGroup.enter()
            .append('g')
            .classed('range-rect', true);
        valuesGroup.attr('transform', d => `translate(${xScale(d.beginDateTime)},0)`);
        enterSelection.append('rect')
            .classed('range', true)
            .style('opacity', 1);
        valuesGroup
            .select('rect.range')
            .attr({
                'width': d => xScale(d.endDateTime) - xScale(d.beginDateTime),
                'height': height
            })
            .style({ 'fill': d => d.color });
        enterSelection.append('title').classed('range-title', true);
        valuesGroup.select('title.range-title')
            .text(d => this.formatRange(d));
    }

    private formatRange(range: { name: string; beginDateTime: Date; endDateTime: Date }): string {
        return `${range.name} (${this._dateFormat(range.beginDateTime)} - ${this._dateFormat(range.endDateTime)})`;
    }

    private stateCrosshair(selection: d3.Selection<any>) {
        const group = selection
            .append('g')
            .classed('state-crosshair', true);
        group.append('rect')
            .classed('crosshair-bg', true)
            .style('visibility', 'hidden')
            .style('pointer-events', 'all');
        group.append('title');
        group.append('rect')
            .classed('crosshair', true)
            .style('pointer-events', 'none')
            .attr('width', 2);
    }

    private updateCrosshairLayout(selection: d3.Selection<any>) {
        const self = this;
        const group = selection.select('g.state-crosshair')
            .classed('hidden-group', this._mode === StatusChartMode.Frequency);
        group.select('rect.crosshair-bg')
            .attr('width', self._rectWidth)
            .attr('height', self._rectHeight)
            .on('mousemove', (d) => {
                const target = (d3.event as Event).target;
                const mouseX = d3.mouse(target)[0];
                const date = self._timeScale.invert(mouseX);
                self.group().selectAll('.crosshair')
                    .attr('transform', `translate(${RoundingHelper.notNaN(self._timeScale(date), 0)},0)`);
                group
                    .select('title')
                    .text(() => {
                        const found = _.find(d.ranges, (r: any) => moment(r.beginDateTime) <= moment(date)
                            && moment(r.endDateTime) >= moment(date));
                        const name = found ? self.formatRange(found) : '';
                        return name;
                    });
                self._dispatch.dateChanged(date);
            })
            .on('mouseleave', () => self._dispatch.dateChanged(null));
        group.select('rect.crosshair')
            .attr('height', self._rectHeight);
    }

    update(data: any[]) {
        this._lastData = data;
        this.computeSizes(this.width(), this.height());
        const dataSelection = this.group().selectAll('.group-result')
            .data(data);
        // EXIT
        dataSelection.exit()
            .remove();
        // ENTER
        this.enter(dataSelection);
        const ranges = _.chain(data)
            .map(d => d.ranges)
            .flatten()
            .map(d => [new Date(d.beginDateTime), new Date(d.endDateTime)])
            .flatten()
            .value() as Date[];
        const domain = d3.extent(ranges);
        this._timeScale.domain(domain);
        if (this.isLegendVisible()) {
            const legendStates = this.getLegendStates(data);
            this.legend().series(legendStates);
        }
        // UPDATE
        this.updateLayout(dataSelection);
    }

    private enter(dataSelection: d3.selection.Update<any>) {
        const enterSelection = dataSelection
            .enter()
            .append('g')
            .classed('group-result', true)
            .append('g')
            .classed('container', true)
            .attr('transform', `translate(20, 5)`);
        this.createGroupTitle(enterSelection);
        const valueGroup = enterSelection
            .append('g')
            .classed('group-value', true);
        valueGroup.append('rect')
            .classed('border', true)
            .attr({
                width: this._rectWidth,
                height: this._rectHeight
            })
            .style({
                'fill': 'none',
                'stroke': greyTextColor
            });
        valueGroup.append('g')
            .classed('values-rect', true)
            .style('visibility', this._mode === StatusChartMode.Frequency ? 'visible' : 'hidden')
            .style('display', this._mode === StatusChartMode.Frequency ? null : 'none');
        const rangesRect = valueGroup.append('g')
            .classed('ranges-rect', true)
            .style('visibility', this._mode === StatusChartMode.Chronological ? 'visible' : 'hidden')
            .style('display', this._mode === StatusChartMode.Chronological ? null : 'none');
        rangesRect.append('g')
            .classed('axis', true)
            .classed('time-axis', true);
        valueGroup.call(this.stateCrosshair.bind(this));
    }

    private createGroupTitle(selection: d3.Selection<any>): d3.Selection<any> {
        const groupTitles = selection
            .append('g')
            .classed('group-title', true)
            .classed('heading', true)
            .attr('transform', `translate(0, ${this._textHeight})`);
        groupTitles.append('text');
        return groupTitles;
    }

    private updateLayout(selection: d3.Selection<any>) {
        if (selection.length) {
            const ratio = this.ratio() || 1;
            const count = selection[0].length;
            const config = getConfig(count, this.width(), this.height(), this._margin * ratio, ratio);
            selection
                .transition()
                .attr('transform', (d, i) => {
                    return `translate(${this._mode === StatusChartMode.Chronological ? 40 * ratio : 0}, ${i * (config.height)})`;
                });
            this._manager.setHeight(this.height());
            const headingTranslate = `translate(${0}, ${config.headingHeight / 2})`;
            selection.select('.group-title').remove();
            const container = selection.select('.container')
                .attr('transform', `translate(${20 * this.ratio()}, ${5 * this.ratio()})`);
            const groupTitles = this.createGroupTitle(container)
                .classed('clickable', d => !!d.callback)
                .on('click', d => {
                    if (d.callback) {
                        d.callback();
                    }
                })
                .attr('transform', headingTranslate);
            groupTitles.select('text')
                .text(d => d.name);
            groupTitles.each(function (d) {
                const selection = d3.select(this);
                selection.attr(d.attributes);
            });
            selection
                .selectAll('rect.border')
                .attr('height', config.rectHeight);
            selection
                .selectAll('g.group-value')
                .attr('transform', () => {
                    return `translate(${0},${config.headingHeight})`;
                });
            this.drawValues(selection.select('.values-rect'), this._rectWidth, config.rectHeight);
            const select = selection.select('.ranges-rect');
            this.drawRanges(select, this._rectWidth, config.rectHeight);
            const axisSelection = selection.selectAll('g.axis');
            axisSelection
                .attr('transform', `translate(0,${config.rectHeight})`);
            const ticks = selection.selectAll('.tick');
            ticks.select('text')
                .attr('y', 3 + config.tickHeight)
                .classed('tickLabel', true);
            ticks.select('line')
                .attr('y2', config.tickHeight);
            this.updateCrosshairLayout(selection);
        }
    }

    mode(value?: StatusChartMode): StatusChartMode {
        if (arguments.length) {
            const ratio = this.ratio() || 1;
            this.computeSizes(this.width(), this.height());
            this._mode = value;
            switch (this._mode) {
                case StatusChartMode.Frequency:
                    this.group().selectAll('.values-rect')
                        .style('visibility', 'visible')
                        .style('display', null);
                    this.group().selectAll('.ranges-rect')
                        .style('visibility', 'hidden')
                        .style('display', 'none')
                        ;
                    this._groupMargins.left = 21 * ratio;
                    this.group().selectAll('.state-crosshair')
                        .classed('hidden-group', true);
                    break;
                case StatusChartMode.Chronological:
                    this.group().selectAll('.values-rect')
                        .style('visibility', 'hidden')
                        .style('display', 'none');
                    this.group().selectAll('.ranges-rect')
                        .style('visibility', 'visible')
                        .style('display', null);
                    this._groupMargins.left = 60 * ratio;
                    this.group().selectAll('.state-crosshair').classed('hidden-group', false);
                    break;
                default:
                    break;
            }
            this.group().selectAll('.group-result')
                .attr('transform',
                    (_d, i) => `translate(${this._groupMargins.left},${i * (this._groupHeight)
                        + this._groupMargins.top})`);
        }
        return this._mode;
    }

    updateCrosshair(date: Date) {
        if (date && this._mode === StatusChartMode.Chronological) {
            this.group()
                .selectAll('.crosshair')
                .classed('hidden-group', false)
                .attr('transform', `translate(${RoundingHelper.notNaN(this._timeScale(date), 0)},0)`);
        } else {
            this.group().selectAll('.crosshair')
                .classed('hidden-group', true);
        }
    }

    onDateChanged(callback) {
        this._dispatch.on('dateChanged', callback);
    }


    private removePanes(): void {
        this.group().selectAll('.plot-area').remove();
    }

    private getLegendStates(data: any[]): FakeSeries[] {
        let legendStates: FakeSeries[] = [];
        if (data) {
            legendStates = _.flatten(data.map(d => d.legends)).map(r => {
                return {
                    name: r.name,
                    legendText: () => r.name,
                    color: () => r.color,
                    identifier: () => r.guid,
                    legendAttributes: () => [],
                    isVisible: true
                } as any;
            });
        }
        legendStates = _.uniqBy(legendStates, 'name');
        return _.sortBy(legendStates, ls => ls.name);
    }
}